import type { T_HoldingsExtractionResult } from "@lattice/core-lib/types/onboarding/holdings-upload.types";
import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, fromPromise, setup } from "xstate";

export type T_PortfolioDraftHolding = {
  isCrypto: boolean;
  symbol: string;
  units: number;
};

export type T_PortfolioDraft = {
  cashBalance: number;
  holdings: T_PortfolioDraftHolding[];
  liabilityBalance: number;
};

export type T_PortfolioImportSource = "camera" | "file" | "library";
export type T_PortfolioInitialImport = "file" | "library" | "photo";

export type T_PortfolioImportResult =
  | { status: "cancelled" }
  | {
      durationMs: number;
      result: T_HoldingsExtractionResult;
      source: T_PortfolioImportSource;
      status: "imported";
    };

export type T_PortfolioDraftMachineInput = {
  importHoldings: (
    source: T_PortfolioImportSource,
    signal: AbortSignal,
  ) => Promise<T_PortfolioImportResult>;
  initialImport: T_PortfolioInitialImport | null;
  initialDraft: T_PortfolioDraft;
  onImportCancelled: (source: T_PortfolioImportSource) => void;
  onImportFailed: (source: T_PortfolioImportSource) => void;
  onImported: (result: T_PortfolioImportResult) => void;
  onSaved: (draft: T_PortfolioDraft) => void;
  saveDraft: (draft: T_PortfolioDraft) => Promise<void>;
};

type T_PortfolioDraftContext = {
  cashInput: string;
  error: string | null;
  holdings: T_PortfolioDraftHolding[];
  importSource: T_PortfolioImportSource | null;
  input: T_PortfolioDraftMachineInput;
  liabilityInput: string;
};

type T_PortfolioDraftEvent =
  | { type: "OPEN_IMPORT" }
  | { type: "CLOSE_IMPORT" }
  | { source: T_PortfolioImportSource; type: "IMPORT" }
  | { type: "RETRY_IMPORT" }
  | { holding: T_PortfolioDraftHolding; type: "UPSERT_HOLDING" }
  | { symbol: string; type: "REMOVE_HOLDING" }
  | { value: string; type: "UPDATE_CASH" }
  | { value: string; type: "UPDATE_LIABILITY" }
  | { type: "SAVE" }
  | { type: "DISMISS_ERROR" };

type T_ImportActorInput = {
  importHoldings: T_PortfolioDraftMachineInput["importHoldings"];
  source: T_PortfolioImportSource | null;
};

type T_SaveActorInput = {
  draft: T_PortfolioDraft;
  saveDraft: T_PortfolioDraftMachineInput["saveDraft"];
};

const types: SetupTypes<
  T_PortfolioDraftContext,
  T_PortfolioDraftEvent,
  Record<never, string>,
  string,
  T_PortfolioDraftMachineInput,
  unknown,
  EventObject,
  MetaObject
> = {};

function parseBalanceInput(value: string): number {
  if (value.trim().length === 0) {
    return 0;
  }
  return Number.parseFloat(value);
}

function formatBalanceInput(value: number): string {
  return value > 0 ? String(value) : "";
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}

export function mergePortfolioDraftHoldings(
  existing: ReadonlyArray<T_PortfolioDraftHolding>,
  imported: ReadonlyArray<T_HoldingsExtractionResult["holdings"][number]>,
): T_PortfolioDraftHolding[] {
  const bySymbol = new Map<string, T_PortfolioDraftHolding>();
  for (const holding of existing) {
    bySymbol.set(normalizeSymbol(holding.symbol), holding);
  }
  for (const holding of imported) {
    const symbol = normalizeSymbol(holding.symbol);
    bySymbol.set(symbol, {
      isCrypto: holding.isCrypto,
      symbol,
      units: holding.units,
    });
  }
  return [...bySymbol.values()];
}

export function getPortfolioDraft(
  context: Pick<
    T_PortfolioDraftContext,
    "cashInput" | "holdings" | "liabilityInput"
  >,
): T_PortfolioDraft {
  return {
    cashBalance: parseBalanceInput(context.cashInput),
    holdings: context.holdings,
    liabilityBalance: parseBalanceInput(context.liabilityInput),
  };
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

function isValidBalanceInput(value: string): boolean {
  const parsed = parseBalanceInput(value);
  return Number.isFinite(parsed) && parsed >= 0;
}

export const portfolioDraftMachine = setup({
  types,
  actors: {
    importHoldings: fromPromise<T_PortfolioImportResult, T_ImportActorInput>(
      async ({ input, signal }) => {
        if (input.source === null) {
          throw new Error("Choose a photo or file to import.");
        }
        return await input.importHoldings(input.source, signal);
      },
    ),
    saveDraft: fromPromise<undefined, T_SaveActorInput>(async ({ input }) => {
      await input.saveDraft(input.draft);
      return undefined;
    }),
  },
  actions: {
    applyImportedHoldings: assign(
      (
        { context },
        result: Extract<T_PortfolioImportResult, { status: "imported" }>,
      ) => ({
        error: null,
        holdings: mergePortfolioDraftHoldings(
          context.holdings,
          result.result.holdings,
        ),
        importSource: null,
      }),
    ),
    clearError: assign({ error: null }),
    clearImport: assign({ error: null, importSource: null }),
    notifyImportCancelled: ({ context }) => {
      if (context.importSource !== null) {
        context.input.onImportCancelled(context.importSource);
      }
    },
    notifyImportFailed: ({ context }) => {
      if (context.importSource !== null) {
        context.input.onImportFailed(context.importSource);
      }
    },
    notifyImported: (
      { context },
      result: Extract<T_PortfolioImportResult, { status: "imported" }>,
    ) => {
      context.input.onImported(result);
    },
    notifySaved: ({ context }) => {
      context.input.onSaved(getPortfolioDraft(context));
    },
    removeHolding: assign({
      holdings: ({ context, event }) =>
        event.type === "REMOVE_HOLDING"
          ? context.holdings.filter(
              (holding) => holding.symbol !== normalizeSymbol(event.symbol),
            )
          : context.holdings,
    }),
    setError: assign({
      error: (_, params: { error: unknown }) => getErrorMessage(params.error),
    }),
    setImportSource: assign({
      error: null,
      importSource: ({ event }) =>
        event.type === "IMPORT" ? event.source : null,
    }),
    updateCash: assign({
      cashInput: ({ context, event }) =>
        event.type === "UPDATE_CASH" ? event.value : context.cashInput,
    }),
    updateLiability: assign({
      liabilityInput: ({ context, event }) =>
        event.type === "UPDATE_LIABILITY"
          ? event.value
          : context.liabilityInput,
    }),
    upsertHolding: assign({
      holdings: ({ context, event }) =>
        event.type === "UPSERT_HOLDING"
          ? mergePortfolioDraftHoldings(context.holdings, [
              {
                isCrypto: event.holding.isCrypto,
                symbol: event.holding.symbol,
                units: event.holding.units,
              },
            ])
          : context.holdings,
    }),
  },
  guards: {
    canSave: ({ context }) =>
      isValidBalanceInput(context.cashInput) &&
      isValidBalanceInput(context.liabilityInput),
    importWasCancelled: (_, params: T_PortfolioImportResult) =>
      params.status === "cancelled",
    startsWithFileImport: ({ context }) =>
      context.input.initialImport === "file",
    startsWithLibraryImport: ({ context }) =>
      context.input.initialImport === "library",
    startsWithPhotoImport: ({ context }) =>
      context.input.initialImport === "photo",
  },
}).createMachine({
  id: "portfolioDraft",
  context: ({ input }) => ({
    cashInput: formatBalanceInput(input.initialDraft.cashBalance),
    error: null,
    holdings: input.initialDraft.holdings,
    importSource:
      input.initialImport === "file"
        ? "file"
        : input.initialImport === "library"
          ? "library"
          : null,
    input,
    liabilityInput: formatBalanceInput(input.initialDraft.liabilityBalance),
  }),
  initial: "starting",
  states: {
    starting: {
      always: [
        { guard: "startsWithFileImport", target: "importing" },
        { guard: "startsWithLibraryImport", target: "importing" },
        { guard: "startsWithPhotoImport", target: "choosingImport" },
        { target: "editing" },
      ],
    },
    editing: {
      on: {
        OPEN_IMPORT: { target: "choosingImport" },
        REMOVE_HOLDING: { actions: "removeHolding" },
        SAVE: { guard: "canSave", target: "saving" },
        UPDATE_CASH: { actions: "updateCash" },
        UPDATE_LIABILITY: { actions: "updateLiability" },
        UPSERT_HOLDING: { actions: "upsertHolding" },
      },
    },
    choosingImport: {
      on: {
        CLOSE_IMPORT: { actions: "clearImport", target: "editing" },
        IMPORT: { actions: "setImportSource", target: "importing" },
      },
    },
    importing: {
      invoke: {
        src: "importHoldings",
        input: ({ context }) => ({
          importHoldings: context.input.importHoldings,
          source: context.importSource,
        }),
        onDone: [
          {
            guard: {
              type: "importWasCancelled",
              params: ({ event }) => event.output,
            },
            actions: ["notifyImportCancelled", "clearImport"],
            target: "editing",
          },
          {
            actions: [
              {
                type: "applyImportedHoldings",
                params: ({ event }) => {
                  if (event.output.status === "cancelled") {
                    throw new Error("Cancelled imports cannot be applied.");
                  }
                  return event.output;
                },
              },
              {
                type: "notifyImported",
                params: ({ event }) => {
                  if (event.output.status === "cancelled") {
                    throw new Error("Cancelled imports cannot be reported.");
                  }
                  return event.output;
                },
              },
            ],
            target: "editing",
          },
        ],
        onError: {
          actions: [
            {
              type: "setError",
              params: ({ event }) => ({ error: event.error }),
            },
            "notifyImportFailed",
          ],
          target: "importFailed",
        },
      },
      on: {
        CLOSE_IMPORT: {
          actions: ["notifyImportCancelled", "clearImport"],
          target: "editing",
        },
      },
    },
    importFailed: {
      on: {
        CLOSE_IMPORT: { actions: "clearImport", target: "editing" },
        RETRY_IMPORT: { actions: "clearError", target: "importing" },
      },
    },
    saving: {
      invoke: {
        src: "saveDraft",
        input: ({ context }) => ({
          draft: getPortfolioDraft(context),
          saveDraft: context.input.saveDraft,
        }),
        onDone: {
          actions: ["clearError", "notifySaved"],
          target: "editing",
        },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "saveFailed",
        },
      },
    },
    saveFailed: {
      on: {
        DISMISS_ERROR: { actions: "clearError", target: "editing" },
        SAVE: { guard: "canSave", target: "saving" },
      },
    },
  },
});
